import React, { useContext, useEffect } from "react";
import { AnySourceData } from "mapbox-gl";

import { MapContext } from "fond/map/MapProvider";

interface IProps {
  /**
   * A unique layer id.
   */
  id: string;
  /*
    The layer's type
  */
  type: any;
  /**
   * The layers paint styles.
   */
  paint: any;
  /**
   * The layers source
   */
  source: string | AnySourceData;
  /**
   * Vector tile source layer
   */
  sourceLayer: string;
  /**
   * The minimum zoom level at which the layer is drawn.
   */
  minzoom?: number;
  /**
   * The maximum zoom level at which the layer is drawn.
   */
  maxzoom?: number;
}

const VectorLayer: React.FC<IProps> = ({ id, paint, source, sourceLayer, type, minzoom, maxzoom }: IProps) => {
  const { map } = useContext(MapContext);

  useEffect(() => {
    add();
    map?.on("styledata", onStyleDataChange);

    return () => {
      remove();
      map?.off("styledata", onStyleDataChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onStyleDataChange = () => {
    if (!map?.getLayer(id)) {
      remove();
      add();
    }
  };

  const remove = () => {
    if (map?.getLayer(id)) {
      map?.removeLayer(id);
    }
    // A mapbox source (with id = this.props.id) gets created by addLayer. It should also be removed.
    /* TODO: If a pre-existing source was passed in consider:
       - Not removing it OR
       - Using a parameter to control it's removal
     */
    if (map?.getSource(id)) {
      map?.removeSource(id);
    }
  };

  const add = () => {
    map?.addLayer({
      id,
      type,
      source,
      "source-layer": sourceLayer,
      // mapbox refuses to show anything if you pass in minzoom/maxzoom with undefined values, so
      // be a little careful with those parameters.
      ...(minzoom !== undefined ? { minzoom } : {}),
      ...(maxzoom !== undefined ? { maxzoom } : {}),
      layout: {},
      paint,
    });
  };

  return null;
};

export default VectorLayer;
